/*!
 * (c) 2026 Ocean Essentials Limited (HK). All rights reserved.
 * Proprietary code. Licensed, not sold. See LICENSE.md and Services Agreement.
 */

/**
 * ============================================
 * SEARCH MODAL 2026
 * Predictive Search Modal Component
 * ============================================
 *
 * Features:
 * - Debounced predictive search
 * - Keyboard navigation (arrows, enter, escape)
 * - Focus trap for accessibility
 * - Recent searches (localStorage)
 * - Multiple result types (products, collections, pages, articles)
 *
 * Usage:
 * - Add data-open-search attribute to trigger buttons
 * - Use Cmd/Ctrl + K keyboard shortcut
 *
 * Events:
 * - search-modal:open - Modal opened
 * - search-modal:close - Modal closed
 * - search-modal:search - Search performed
 * - search-modal:select - Result selected
 */

class SearchModal2026 extends HTMLElement {
  constructor() {
    super();

    // Configuration
    // NaN check (not ||) so a merchant-set limit of 0 is honored, not replaced by the default
    const intOr = (value, fallback) => {
      const parsed = parseInt(value, 10);
      return Number.isNaN(parsed) ? fallback : parsed;
    };
    this.config = {
      predictiveSearchUrl: this.dataset.predictiveSearchUrl,
      searchUrl: this.dataset.searchUrl,
      productsLimit: parseInt(this.dataset.productsLimit, 10) || 4,
      collectionsLimit: intOr(this.dataset.collectionsLimit, 2),
      pagesLimit: intOr(this.dataset.pagesLimit, 2),
      debounceDelay: parseInt(this.dataset.debounce, 10) || 300,
      maxRecent: parseInt(this.dataset.maxRecent, 10) || 5,
      sectionId: this.dataset.sectionId,
    };

    // State
    this.isOpen = false;
    this.currentQuery = "";
    this.focusedIndex = -1;
    this.resultItems = [];
    this.abortController = null;
    this.debounceTimer = null;
    this.previousActiveElement = null;

    // Elements
    this.overlay = this.querySelector("[data-search-overlay]");
    this.modal = this.querySelector("[data-search-modal]");
    this.input = this.querySelector("[data-search-input]");
    this.clearBtn = this.querySelector("[data-clear-btn]");
    this.closeBtn = this.querySelector("[data-close-btn]");
    this.content = this.querySelector("[data-search-content]");

    // State elements
    this.states = {
      initial: this.querySelector('[data-state="initial"]'),
      loading: this.querySelector('[data-state="loading"]'),
      results: this.querySelector('[data-state="results"]'),
      noResults: this.querySelector('[data-state="no-results"]'),
    };

    // Results elements
    this.productsSection = this.querySelector("[data-products-section]");
    this.productsList = this.querySelector("[data-products-list]");
    this.collectionsSection = this.querySelector("[data-collections-section]");
    this.collectionsList = this.querySelector("[data-collections-list]");
    this.pagesSection = this.querySelector("[data-pages-section]");
    this.pagesList = this.querySelector("[data-pages-list]");
    this.viewAllLink = this.querySelector("[data-view-all]");
    this.viewAllQuery = this.querySelector("[data-view-all-query]");
    this.noResultsQuery = this.querySelector("[data-no-results-query]");

    // Recent searches
    this.recentSearchesSection = this.querySelector("[data-recent-searches]");
    this.recentSearchesList = this.querySelector("[data-recent-list]");

    // Templates
    this.productTemplate = document.getElementById(`search-product-template-${this.config.sectionId}`);
    this.suggestionTemplate = document.getElementById(`search-suggestion-template-${this.config.sectionId}`);
    this.recentTemplate = document.getElementById(`search-recent-template-${this.config.sectionId}`);

  }

  connectedCallback() {
    if (this.events && !this.events.signal.aborted) return;
    this.events = new AbortController();
    this.bindEvents();
    this.loadRecentSearches();
    this.listen(document, "shopify:section:unload", (event) => {
      if (event.target.contains(this) || event.detail?.sectionId === this.config.sectionId) {
        this.close();
        this.events.abort();
        clearTimeout(this.debounceTimer);
        clearTimeout(this.focusTimer);
        this.abortController?.abort();
      }
    });
  }

  listen(target, type, handler) {
    target?.addEventListener(type, handler, { signal: this.events.signal });
  }

  /**
   * Bind all event listeners
   */
  bindEvents() {
    // Modal controls
    this.listen(this.overlay, "click", () => this.close());
    this.listen(this.closeBtn, "click", () => this.close());
    this.listen(this.clearBtn, "click", () => this.clearSearch());

    // Input events
    this.listen(this.input, "input", (e) => this.handleInput(e));
    this.listen(this.input, "keydown", (e) => this.handleKeydown(e));

    // Quick link clicks (popular searches)
    this.listen(this, "click", (e) => {
      const quickLink = e.target.closest("[data-search-query]");
      if (quickLink) {
        e.preventDefault();
        const query = quickLink.dataset.searchQuery;
        this.input.value = query;
        this.performSearch(query);
      }

      // Remove recent search
      const removeBtn = e.target.closest("[data-remove-recent]");
      if (removeBtn) {
        e.preventDefault();
        e.stopPropagation();
        const recentItem = removeBtn.closest("[data-recent-item]");
        if (recentItem) {
          const query = recentItem.querySelector("[data-search-query]")?.dataset.searchQuery;
          if (query) {
            this.removeRecentSearch(query);
            recentItem.remove();
            this.updateRecentSearchesVisibility();
          }
        }
      }
    });

    // Global keyboard shortcuts (named handler so disconnectedCallback can remove it)
    this._globalKeydownHandler = (e) => {
      // Cmd/Ctrl + K to open
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        this.toggle();
      }

      // Escape to close
      if (e.key === "Escape" && this.isOpen) {
        e.preventDefault();
        this.close();
      }
    };
    this.listen(document, "keydown", this._globalKeydownHandler);

    // Open search triggers
    this.listen(document, "click", (e) => {
      if (e.target.closest("[data-open-search]")) {
        e.preventDefault();
        this.open();
      }
    });
  }

  /**
   * Cleanup when removed from the DOM (e.g. theme editor section re-render).
   * Deferred: open() re-appends a connected element to body, which fires this
   * callback synchronously mid-move — only clean up if still disconnected.
   */
  disconnectedCallback() {
    queueMicrotask(() => {
      if (this.isConnected) return;
      this.events?.abort();
      clearTimeout(this.debounceTimer);
      clearTimeout(this.focusTimer);
      this.abortController?.abort();
      document.removeEventListener("keydown", this._globalKeydownHandler);
      if (this._tabTrapHandler) {
        document.removeEventListener("keydown", this._tabTrapHandler, true);
        this._tabTrapHandler = null;
      }
      if (this._inertedSiblings) {
        this._inertedSiblings.forEach((el) => el.removeAttribute("inert"));
        this._inertedSiblings = null;
        document.body.style.overflow = "";
      }
    });
  }

  /**
   * Open the search modal
   */
  open() {
    // Stale trigger listeners from a replaced instance must be no-ops
    if (!this.isConnected) return;
    if (this.isOpen) return;

    // Move to body root to escape any containing block issues (grid, transforms)
    if (this.parentElement && this.parentElement !== document.body) {
      this._originalParent = this.parentElement;
      this._originalNextSibling = this.nextSibling;
      document.body.appendChild(this);
    }

    this.previousActiveElement = document.activeElement;
    this.isOpen = true;
    this.setAttribute("aria-hidden", "false");
    document.body.style.overflow = "hidden";

    // Focus trap: mark all body siblings as inert so Tab skips them entirely
    this._inertedSiblings = [];
    Array.from(document.body.children).forEach((el) => {
      if (el !== this && !el.hasAttribute("inert")) {
        el.setAttribute("inert", "");
        this._inertedSiblings.push(el);
      }
    });

    // Document-level Tab handler as fallback for older browsers without inert support
    this._tabTrapHandler = (e) => {
      if (e.key !== "Tab" || !this.isOpen) return;
      const focusable = this.getFocusableElements();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const active = document.activeElement;
      if (!this.contains(active)) {
        e.preventDefault();
        (e.shiftKey ? last : first).focus();
      } else if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", this._tabTrapHandler, true);

    // Focus input after animation
    requestAnimationFrame(() => {
      this.focusTimer = setTimeout(() => {
        if (this.isOpen && this.isConnected) this.input?.focus();
      }, 100);
    });

    this.dispatchEvent(new CustomEvent("search-modal:open", { bubbles: true }));
  }

  /**
   * Get all currently focusable elements inside the modal
   * (filters out hidden, disabled, tabindex="-1")
   */
  getFocusableElements() {
    const selector = [
      'a[href]:not([aria-hidden="true"])',
      "button:not([disabled]):not([hidden])",
      "input:not([disabled]):not([hidden])",
      "select:not([disabled])",
      "textarea:not([disabled])",
      '[tabindex]:not([tabindex="-1"])',
    ].join(",");
    return Array.from(this.querySelectorAll(selector)).filter((el) => {
      // Exclude elements with display:none or visibility:hidden
      const style = window.getComputedStyle(el);
      return style.display !== "none" && style.visibility !== "hidden" && !el.closest("[hidden]");
    });
  }

  /**
   * Close the search modal
   */
  close() {
    if (!this.isOpen) return;

    this.isOpen = false;
    this.setAttribute("aria-hidden", "true");
    document.body.style.overflow = "";

    // Release focus trap: restore inert siblings and remove Tab handler
    if (this._inertedSiblings) {
      this._inertedSiblings.forEach((el) => el.removeAttribute("inert"));
      this._inertedSiblings = null;
    }
    if (this._tabTrapHandler) {
      document.removeEventListener("keydown", this._tabTrapHandler, true);
      this._tabTrapHandler = null;
    }

    // Restore to original DOM position
    if (this._originalParent) {
      if (this._originalNextSibling) {
        this._originalParent.insertBefore(this, this._originalNextSibling);
      } else {
        this._originalParent.appendChild(this);
      }
      this._originalParent = null;
      this._originalNextSibling = null;
    }

    // Reset state
    this.clearSearch();
    this.showState("initial");

    // Restore focus
    this.previousActiveElement?.focus();

    this.dispatchEvent(new CustomEvent("search-modal:close", { bubbles: true }));
  }

  /**
   * Toggle modal open/close
   */
  toggle() {
    if (this.isOpen) {
      this.close();
    } else {
      this.open();
    }
  }

  /**
   * Clear search input and reset
   */
  clearSearch() {
    this.input.value = "";
    this.currentQuery = "";
    this.clearBtn.hidden = true;
    this.input?.focus();
    this.showState("initial");
    this.abortCurrentSearch();
  }

  /**
   * Handle input changes with debounce
   */
  handleInput(e) {
    const query = e.target.value.trim();
    this.clearBtn.hidden = query.length === 0;

    // Clear previous timer
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
    }

    if (query.length === 0) {
      this.currentQuery = "";
      this.showState("initial");
      this.abortCurrentSearch();
      return;
    }

    // Debounce search
    this.debounceTimer = setTimeout(() => {
      this.performSearch(query);
    }, this.config.debounceDelay);
  }

  /**
   * Handle keyboard navigation
   */
  handleKeydown(e) {
    const { key } = e;

    switch (key) {
      case "ArrowDown":
        e.preventDefault();
        this.navigateResults(1);
        break;
      case "ArrowUp":
        e.preventDefault();
        this.navigateResults(-1);
        break;
      case "Enter":
        e.preventDefault();
        this.selectCurrentResult();
        break;
      case "Tab":
        // Allow tab but trap focus within modal
        this.handleTabKey(e);
        break;
    }
  }

  /**
   * Navigate through results with arrow keys
   */
  navigateResults(direction) {
    this.resultItems = Array.from(this.querySelectorAll("[data-result-item]"));

    if (this.resultItems.length === 0) return;

    // Update index
    this.focusedIndex += direction;

    // Wrap around
    if (this.focusedIndex < 0) {
      this.focusedIndex = this.resultItems.length - 1;
    } else if (this.focusedIndex >= this.resultItems.length) {
      this.focusedIndex = 0;
    }

    // Update visual state
    this.resultItems.forEach((item, index) => {
      const isSelected = index === this.focusedIndex;
      item.setAttribute("aria-selected", isSelected.toString());
    });

    // Scroll into view
    this.resultItems[this.focusedIndex]?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
    });
  }

  /**
   * Select the currently focused result
   */
  selectCurrentResult() {
    const query = this.input.value.trim();

    // If a result is focused, navigate to it
    if (this.focusedIndex >= 0 && this.resultItems[this.focusedIndex]) {
      const item = this.resultItems[this.focusedIndex];
      const url = item.href;

      if (url) {
        this.saveRecentSearch(query);
        this.dispatchEvent(
          new CustomEvent("search-modal:select", {
            bubbles: true,
            detail: { url, query },
          }),
        );
        window.location.href = url;
        return;
      }
    }

    // Otherwise, go to full search results
    if (query.length > 0) {
      this.saveRecentSearch(query);
      this.dispatchEvent(
        new CustomEvent("search-modal:search", {
          bubbles: true,
          detail: { query },
        }),
      );
      window.location.href = `${this.config.searchUrl}?q=${encodeURIComponent(query)}`;
    }
  }

  /**
   * Handle tab key for focus trap
   */
  handleTabKey(e) {
    const focusableElements = this.modal.querySelectorAll(
      'button:not([hidden]), [href], input, [tabindex]:not([tabindex="-1"])',
    );
    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey && document.activeElement === firstElement) {
      e.preventDefault();
      lastElement.focus();
    } else if (!e.shiftKey && document.activeElement === lastElement) {
      e.preventDefault();
      firstElement.focus();
    }
  }

  /**
   * Show a specific state
   */
  showState(stateName) {
    Object.entries(this.states).forEach(([name, element]) => {
      if (element) {
        element.hidden = name !== stateName;
      }
    });
    if (this.input) {
      this.input.setAttribute("aria-expanded", stateName === "results" ? "true" : "false");
    }
  }

  /**
   * Abort current search request
   */
  abortCurrentSearch() {
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  /**
   * Perform predictive search
   */
  async performSearch(query) {
    if (query === this.currentQuery) return;

    this.currentQuery = query;
    this.focusedIndex = -1;
    this.abortCurrentSearch();

    if (query.length < 2) {
      this.showState("initial");
      return;
    }

    this.showState("loading");
    this.abortController = new AbortController();

    try {
      // Build resource types as a single combined parameter
      const types = ["product"];
      if (this.config.collectionsLimit > 0) types.push("collection");
      if (this.config.pagesLimit > 0) types.push("page", "article");

      const url = `${this.config.predictiveSearchUrl}?q=${encodeURIComponent(query)}&resources[type]=${types.join(",")}&resources[limit]=${this.config.productsLimit}`;

      const response = await fetch(url, {
        signal: this.abortController.signal,
        headers: {
          Accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error("Search failed");
      }

      const json = await response.json();
      const results = json.resources?.results || json.resources || {};
      this.renderResultsFromJSON(results, query);
    } catch (error) {
      if (error.name === "AbortError") {
        return; // Ignore aborted requests
      }
      console.error("Search error:", error);
      this.showState("noResults");
      if (this.noResultsQuery) {
        this.noResultsQuery.textContent = query;
      }
      // Allow retrying the same query after a failed fetch
      this.currentQuery = "";
    }
  }

  /**
   * Render results from JSON response
   */
  renderResultsFromJSON(resources, query) {
    const products = resources.products || [];
    const collections = resources.collections || [];
    const pages = resources.pages || [];
    const articles = resources.articles || [];

    const hasResults = products.length > 0 || collections.length > 0 || pages.length > 0 || articles.length > 0;

    if (!hasResults) {
      this.showState("noResults");
      if (this.noResultsQuery) {
        this.noResultsQuery.textContent = query;
      }
      return;
    }

    // Render products
    this.renderProducts(products, query);

    // Render collections
    this.renderCollections(collections);

    // Render pages/articles
    this.renderPages([...pages, ...articles]);

    // Update view all link
    this.updateViewAllLink(query);

    this.showState("results");
  }

  /**
   * Render products from JSON data
   */
  renderProducts(products, query) {
    if (!this.productsList || !this.productTemplate) return;

    this.productsList.innerHTML = "";

    if (products.length === 0) {
      this.productsSection.hidden = true;
      return;
    }

    this.productsSection.hidden = false;

    products.forEach((product) => {
      const template = this.productTemplate.content.cloneNode(true);
      const item = template.querySelector(".search-modal__product-item");

      item.href = product.url;

      const thumbEl = template.querySelector(".search-modal__product-thumb");
      const imageUrl = product.image || product.featured_image?.url;
      if (thumbEl && imageUrl) {
        const imgEl = document.createElement("img");
        imgEl.src = imageUrl;
        imgEl.alt = product.featured_image?.alt || product.title;
        imgEl.width = 64;
        imgEl.height = 64;
        imgEl.loading = "lazy";
        imgEl.decoding = "async";
        thumbEl.appendChild(imgEl);
      } else if (thumbEl) {
        thumbEl.hidden = true;
      }

      template.querySelector(".search-modal__product-name").innerHTML = this.highlightQuery(product.title, query);
      template.querySelector(".search-modal__product-price").textContent = this.formatPrice(product.price);

      const typeEl = template.querySelector(".search-modal__product-type");
      if (typeEl && product.type) {
        typeEl.textContent = product.type;
      }

      const vendorEl = template.querySelector(".search-modal__product-vendor");
      if (vendorEl && product.vendor) {
        vendorEl.textContent = product.vendor;
      }

      this.productsList.appendChild(template);
    });
  }

  /**
   * Render collections from JSON data
   */
  renderCollections(collections) {
    if (!this.collectionsList || !this.suggestionTemplate) return;

    this.collectionsList.innerHTML = "";

    if (collections.length === 0 || this.config.collectionsLimit === 0) {
      this.collectionsSection.hidden = true;
      return;
    }

    this.collectionsSection.hidden = false;

    collections.slice(0, this.config.collectionsLimit).forEach((collection) => {
      const template = this.suggestionTemplate.content.cloneNode(true);
      const item = template.querySelector(".search-modal__suggestion-item");

      item.href = collection.url;
      template.querySelector(".search-modal__suggestion-text").textContent = collection.title;

      this.collectionsList.appendChild(template);
    });
  }

  /**
   * Render pages from JSON data
   */
  renderPages(pages) {
    if (!this.pagesList || !this.suggestionTemplate) return;

    this.pagesList.innerHTML = "";

    if (pages.length === 0 || this.config.pagesLimit === 0) {
      this.pagesSection.hidden = true;
      return;
    }

    this.pagesSection.hidden = false;

    pages.slice(0, this.config.pagesLimit).forEach((page) => {
      const template = this.suggestionTemplate.content.cloneNode(true);
      const item = template.querySelector(".search-modal__suggestion-item");

      item.href = page.url;
      template.querySelector(".search-modal__suggestion-text").textContent = page.title;

      this.pagesList.appendChild(template);
    });
  }

  /**
   * Update view all link
   */
  updateViewAllLink(query) {
    if (!this.viewAllLink) return;

    this.viewAllLink.href = `${this.config.searchUrl}?q=${encodeURIComponent(query)}`;
    if (this.viewAllQuery) {
      this.viewAllQuery.textContent = query;
    }
    this.viewAllLink.hidden = false;
  }

  /**
   * Escape HTML to prevent XSS when setting innerHTML
   */
  escapeHtml(str) {
    if (str == null || str === "") return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  /**
   * Highlight search query in text (XSS-safe: escapes HTML before innerHTML)
   */
  highlightQuery(text, query) {
    if (!text) return "";
    const escaped = this.escapeHtml(text);
    if (!query) return escaped;

    const regex = new RegExp(`(${this.escapeRegex(query)})`, "gi");
    return escaped.replace(regex, "<mark>$1</mark>");
  }

  /**
   * Escape regex special characters
   */
  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
  }

  /**
   * Format price for display
   * Shopify Predictive Search API returns price as a string in store currency (e.g. "1999.00"),
   * NOT in cents. No division needed.
   */
  formatPrice(price) {
    const amount = typeof price === "string" ? parseFloat(price) : price;
    if (isNaN(amount)) return "";
    return new Intl.NumberFormat(Shopify.locale || "en", {
      style: "currency",
      currency: Shopify.currency?.active || "EUR",
    }).format(amount);
  }

  /**
   * Load recent searches from localStorage
   */
  loadRecentSearches() {
    if (!this.recentSearchesList || !this.recentTemplate) return;

    const recent = this.getRecentSearches();

    this.recentSearchesList.innerHTML = "";

    if (recent.length === 0) {
      this.recentSearchesSection.hidden = true;
      return;
    }

    this.recentSearchesSection.hidden = false;

    recent.forEach((query) => {
      const template = this.recentTemplate.content.cloneNode(true);
      const btn = template.querySelector("[data-search-query]");

      if (btn) {
        btn.dataset.searchQuery = query;
        template.querySelector(".search-modal__recent-text").textContent = query;
      }

      this.recentSearchesList.appendChild(template);
    });
  }

  /**
   * Get recent searches from localStorage
   */
  getRecentSearches() {
    try {
      const stored = localStorage.getItem("exocet_recent_searches");
      const parsed = stored ? JSON.parse(stored) : [];
      return Array.isArray(parsed) ? parsed.filter((query) => typeof query === "string").slice(0, this.config.maxRecent) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Save a search to recent searches
   */
  saveRecentSearch(query) {
    if (!query || query.length < 2) return;

    try {
      let recent = this.getRecentSearches();

      // Remove if already exists
      recent = recent.filter((q) => q.toLowerCase() !== query.toLowerCase());

      // Add to beginning
      recent.unshift(query);

      // Limit to max
      recent = recent.slice(0, this.config.maxRecent);

      localStorage.setItem("exocet_recent_searches", JSON.stringify(recent));
    } catch (e) {
      // localStorage not available
    }
  }

  /**
   * Remove a search from recent searches
   */
  removeRecentSearch(query) {
    try {
      let recent = this.getRecentSearches();
      recent = recent.filter((q) => q.toLowerCase() !== query.toLowerCase());
      localStorage.setItem("exocet_recent_searches", JSON.stringify(recent));
    } catch (e) {
      // localStorage not available
    }
  }

  /**
   * Update recent searches section visibility
   */
  updateRecentSearchesVisibility() {
    const recent = this.getRecentSearches();
    if (this.recentSearchesSection) {
      this.recentSearchesSection.hidden = recent.length === 0;
    }
  }
}

// Register custom element
if (!customElements.get("search-modal-2026")) {
  customElements.define("search-modal-2026", SearchModal2026);
}
